import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useContent } from "../cms/store.js";
import { useSeo } from "../seo.js";
import { useServices } from "../hooks.js";
import { resetConsent } from "../consent.js";
import NotFound from "../pages/site/NotFound.jsx";
import LandingStart from "./LandingStart.jsx";
import {
  STRAPLINE,
  TRUST_CHIPS,
  TOPICS_NOTE,
  fillTerm,
  heroMoreHtml,
  heroPriceLine,
  includedHtml,
  KIND_LABEL,
  KINDS,
  pickService,
  priceCardsHtml,
  receiveHtml,
  providerLd,
  REVIEW_AVOID,
  reviewTopic,
  slugPath,
  stepsHtml,
  zl,
} from "./landingCopy.mjs";
import { lpDuoHtml, personCardHtml, resolvePeople, reviewsHtml } from "./trust.mjs";
import { funnel } from "../funnel.js";
import { SITE_URL } from "../seo.js";

/*
 * Strona docelowa pod reklamy Google Ads (Search): jeden szablon, treść z
 * konfiguracji (CMS: gałąź "landings", edytowana w /admin/landingi; fabryczne
 * wartości w landing/landings.json).
 *
 * DWA TRYBY pierwszego ekranu (nagłówek + hero):
 *  • z prerenderu (scripts/prerender-landings.mjs): HTML ma już #lp-shell —
 *    statyczny pierwszy ekran POZA #root. Zostaje w DOM-ie na stałe (React go
 *    nie zastępuje, dzięki czemu LCP liczy się od jego pierwszego malowania, a
 *    nie od momentu załadowania bundla) — tu tylko wstawiamy do #zamow
 *    formularz (portal) i uzupełniamy teksty, jeśli CMS ma nowsze;
 *  • bez prerenderu (npm start, wejście z innej strony serwisu): React
 *    renderuje ten sam pierwszy ekran sam, tymi samymi klasami lp-*.
 * Reszta strony (poniżej pierwszego ekranu) zawsze jest z Reacta.
 *
 * Własny, lekki układ zamiast globalnego Header/Footer/czatu/chorągiewki (patrz
 * App.js — na tych adresach są wyłączone): mniej wyjść ze strony, mniej
 * skryptów i zapytań, a elementy wiarygodności potrzebne do oceny strony
 * docelowej (dane firmy, polityka prywatności, regulamin, kontakt, zastrzeżenie
 * o charakterze platformy) są w stopce tej strony.
 */

/* Prerender wkleja do HTML-a cennik z chwili budowania jako
   window.__LP_SERVICES__ — pierwszy render pokazuje te same ceny, co statyczny
   pierwszy ekran, zamiast mignąć pustym miejscem do czasu odpowiedzi API. Gdy
   odpowiedź przyjdzie, wygrywa cennik na żywo. */
function useLandingServices() {
  const live = useServices();
  const seeded = typeof window !== "undefined" && Array.isArray(window.__LP_SERVICES__) ? window.__LP_SERVICES__ : [];
  if (live.state === "ready" || !seeded.length) return live;
  return { services: seeded, state: "ready" };
}

/* Zespół w suwaku: zawsze trzy osoby naraz, kolejne po strzałce (i co kilka sekund).
   Bez kropek i licznika — z pierwszego rzutu oka nie widać, ilu ich jest. */
function TeamSlider({ people }) {
  const [start, setStart] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = people.length;
  const reduce = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const show = Math.min(3, n);
  const step = (d) => setStart((v) => (v + d * show + n * 3) % n);

  useEffect(() => {
    if (n <= 3 || reduce || paused) return undefined;
    const t = setInterval(() => setStart((v) => (v + 3) % n), 6000);
    return () => clearInterval(t);
  }, [n, reduce, paused]);

  const visible = Array.from({ length: show }, (_, k) => people[(start + k) % n]);
  return (
    <div
      className="lp-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div
        className="pp-row lp-team"
        key={start}
        aria-live="off"
        dangerouslySetInnerHTML={{ __html: visible.map((p) => personCardHtml(p, { bio: false })).join("") }}
      />
      {n > 3 && (
        <div className="lp-slider-nav">
          <button type="button" onClick={() => step(-1)} aria-label="Poprzedni prawnicy">‹</button>
          <button type="button" onClick={() => step(1)} aria-label="Kolejni prawnicy">›</button>
        </div>
      )}
    </div>
  );
}

/* Przewijana lista ofert w pasku na górze (opinia → dokument → pakiet) —
   pojedyncza cena sugerowałaby, że to jedyna usługa. Przy ograniczonym ruchu
   (prefers-reduced-motion) bez animacji: jedna neutralna linia "od <najniższej ceny>". */
function OfferTicker({ services }) {
  const items = KINDS.map((k) => pickService(services, k)).filter(Boolean);
  const [i, setI] = useState(0);
  const reduce = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  useEffect(() => {
    if (reduce || items.length < 2) return undefined;
    const t = setInterval(() => setI((v) => v + 1), 3200);
    return () => clearInterval(t);
  }, [reduce, items.length]);
  if (!items.length) return null;
  if (reduce || items.length < 2) {
    const min = Math.min(...items.map((s) => s.price_cents));
    return <span className="lp-topbar-price">Usługi prawne od {zl(min)}</span>;
  }
  const s = items[i % items.length];
  return (
    <span className="lp-topbar-price lp-ticker" key={s.kind}>
      {KIND_LABEL[s.kind]} — {zl(s.price_cents)}
    </span>
  );
}

export function useActiveLanding(pathname) {
  const landings = useContent("landings");
  const clean = String(pathname || "").replace(/\/+$/, "") || "/";
  return (Array.isArray(landings) ? landings : []).find((l) => l.active !== false && slugPath(l.slug) === clean) || null;
}

export default function LandingRoute({ slug }) {
  const { landingSlug } = useParams();
  const landing = useActiveLanding(slugPath(slug || landingSlug));
  // Adres z prerenderu, którego CMS już nie ma (strona wyłączona po buildzie) —
  // statyczny pierwszy ekran nie może zostać nad stroną "nie znaleziono".
  useEffect(() => {
    if (!landing) document.getElementById("lp-shell")?.remove();
  }, [landing]);
  if (!landing) return <NotFound />;
  return <Landing landing={landing} />;
}

const goToForm = (e) => {
  e?.preventDefault();
  const card = document.getElementById("zamow");
  if (!card) return;
  const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  card.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  setTimeout(() => document.getElementById("lp-desc")?.focus({ preventScroll: true }), reduce ? 0 : 350);
};

/* Statyczny pierwszy ekran z prerenderu — tylko jeśli to ta sama strona
   (po nawigacji w aplikacji na inną stronę docelową zostałby cudzy). */
function claimShell(slug) {
  if (typeof document === "undefined") return null;
  const el = document.getElementById("lp-shell");
  if (!el) return null;
  if (el.dataset.slug !== slug) {
    el.remove();
    return null;
  }
  return el;
}

function Landing({ landing }) {
  const nav = useNavigate();
  const cennik = landing.layout === "cennik";
  // lejek zamówień: wejście na landing i kliknięcia przycisków prowadzących do formularza (delegacja: działa też dla statycznego pierwszego ekranu)
  useEffect(() => {
    // czas do załadowania aplikacji (od początku wczytywania strony) — kubełek; diagnostyka wolnych telefonów
    const sec = typeof performance !== "undefined" ? performance.now() / 1000 : 0;
    funnel("lp_view", { slug: landing.slug, ttapp: sec < 1 ? "0-1" : sec < 3 ? "1-3" : sec < 6 ? "3-6" : sec < 12 ? "6-12" : "12+" });
    const onClick = (e) => {
      // karta cennika: przejście do zamówienia bez przeładowania strony (źródło wejścia jest już zapisane)
      const card = e.target?.closest?.("a[data-kind]");
      if (card) {
        e.preventDefault();
        funnel("lp_cta", { where: "price_card" });
        nav(card.getAttribute("href"));
        return;
      }
      const a = e.target?.closest?.('a[href="#zamow"]');
      if (!a) return;
      const where = a.classList.contains("lp-hero-cta") ? "hero" : a.closest(".lp-head") ? "header" : a.closest(".lp-topbar") ? "topbar" : "other";
      funnel("lp_cta", { where });
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [landing.slug, nav]);

  // kliknięcie karty cennika przed załadowaniem aplikacji zostało wstrzymane przez loader z prerenderu — kończymy je tutaj
  useEffect(() => {
    window.__lpReady = true;
    const go = window.__lpGo;
    if (!go) return undefined;
    window.__lpGo = null;
    // dopiero po zapisie źródła wejścia (captureAttribution w App.js działa w efekcie rodzica, czyli PO tym efekcie)
    const t = setTimeout(() => nav(go), 0);
    return () => clearTimeout(t);
  }, [nav]);

  const settings = useContent("settings");
  const about = useContent("about");
  const reviews = useContent("googleReviews");
  const team = useContent("team");
  const people = useMemo(() => resolvePeople(team), [team]);
  const shared = useContent("landingShared") || {};
  const { services, state } = useLandingServices();
  const [stickyOn, setStickyOn] = useState(false);
  const [shell] = useState(() => claimShell(landing.slug));
  const focusedStatic = useRef(false);
  const [formHost] = useState(() => {
    const host = shell?.querySelector("#zamow") || null;
    if (host) {
      // ktoś już pisze w statycznym formularzu — po podmianie oddajemy fokus nowemu polu (tekst wraca ze szkicu)
      focusedStatic.current = document.activeElement?.id === "lp-desc";
      host.textContent = ""; // zdejmij statyczny formularz — wchodzi ten sam, żywy
    }
    return host;
  });
  useEffect(() => {
    if (!focusedStatic.current) return;
    const t = document.getElementById("lp-desc");
    if (!t) return;
    t.focus({ preventScroll: true });
    t.setSelectionRange(t.value.length, t.value.length);
  }, []);

  const line = state === "ready" ? heroPriceLine(services, landing.layout) : "";
  const lead = fillTerm(landing.lead, services);
  const prices = KINDS.map((k) => pickService(services, k)?.price_cents).filter(Boolean);
  const fromCents = prices.length ? Math.min(...prices) : 0;

  const jsonLd = useMemo(() => {
    const faq = (landing.faq || []).filter((f) => f.q && f.a).slice(0, 8).map((f) => ({ ...f, a: fillTerm(f.a, services) }));
    const offers = KINDS
      .map((k) => ({ k, s: pickService(services, k) }))
      .filter((x) => x.s)
      .map(({ k, s }) => ({
        "@type": "Offer",
        name: KIND_LABEL[k],
        price: (s.price_cents / 100).toFixed(2),
        priceCurrency: "PLN",
      }));
    const graph = [];
    if (faq.length) {
      graph.push({
        "@type": "FAQPage",
        mainEntity: faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    }
    graph.push({
      "@type": "Service",
      name: landing.h1,
      serviceType: "Usługi prawne online",
      areaServed: "PL",
      provider: providerLd(settings, SITE_URL),
      ...(offers.length ? { offers } : {}),
    });
    return { "@context": "https://schema.org", "@graph": graph };
  }, [landing, services, settings]);

  useSeo({
    title: fillTerm(landing.seoTitle, services),
    description: fillTerm(landing.seoDescription, services),
    path: slugPath(landing.slug),
    jsonLd,
  });

  /* Tryb z prerenderem: statyczny pierwszy ekran zostaje, więc (1) uzupełniamy
     w nim teksty, gdy CMS ma nowsze niż build, (2) przycisk "Zamów" w
     nagłówku przewija i ustawia fokus w formularzu, (3) po opuszczeniu strony
     (nawigacja w aplikacji) zdejmujemy go z DOM-u. */
  useEffect(() => {
    if (!shell) return undefined;
    const setText = (sel, text) => {
      const el = shell.querySelector(sel);
      if (el && text && el.textContent !== text) el.textContent = text;
    };
    setText("h1", landing.h1);
    setText(".lp-lead", lead);
    if (line) setText(".lp-price", line);
    const btn = shell.querySelector('.lp-head a[href="#zamow"]');
    btn?.addEventListener("click", goToForm);
    return () => btn?.removeEventListener("click", goToForm);
  }, [shell, landing.h1, lead, line]);

  useEffect(
    () => () => {
      document.getElementById("lp-shell")?.remove();
    },
    []
  );

  /* Przyklejony przycisk na telefonie pokazujemy dopiero, gdy karta formularza
     jest poza ekranem — gdy jest widoczna, byłby dublem. */
  useEffect(() => {
    const el = document.getElementById("zamow");
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(([entry]) => setStickyOn(!entry.isIntersecting && entry.boundingClientRect.top < 0), {
      threshold: 0,
    });
    io.observe(el);
    return () => io.disconnect();
  }, [shell]);

  const firmName = about?.firm?.name;
  // losowe 3 z całej puli przy każdym wczytaniu strony; link „Zobacz wszystkie opinie w Google” otwiera się w nowej karcie
  const reviewsBlock = useMemo(() => reviewsHtml(reviews, { max: 3, link: true, random: true, topic: reviewTopic(landing.slug), avoid: REVIEW_AVOID }), [reviews, landing.slug]);
  const phoneHref = `tel:${String(settings.phone || "").replace(/\s/g, "")}`;
  const form = <LandingStart landing={landing} services={services} servicesState={state} />;

  return (
    <div className="lp">
      {!shell && (
        <header className="lp-head">
          <div className="lp-wrap lp-head-in">
            <Link to="/" className="logo">
              <b>
                {settings.brand}
                <i>.</i>
              </b>
              <span>{settings.tagline}</span>
            </Link>
            <div className="lp-head-act">
              {settings.phone && (
                <a className="lp-phone" href={phoneHref}>
                  {settings.phone}
                </a>
              )}
              <a href="#zamow" className="btn wax sm" onClick={goToForm}>
                Zamów
              </a>
            </div>
          </div>
        </header>
      )}

      {shell && formHost && createPortal(form, formHost)}

      <main>
        {!shell && (
          <section className="lp-hero">
            <div className="lp-wrap lp-hero-grid">
              <div className="lp-hero-copy">
                <p className="lp-strap">{STRAPLINE}</p>
                <h1>{landing.h1}</h1>
                <p className="lp-lead">{lead}</p>
                <p className="lp-price">{line}</p>
                <a href="#zamow" className="btn wax lp-hero-cta" onClick={goToForm}>
                  Opisz sprawę
                </a>
                <ul className="lp-chips">
                  {TRUST_CHIPS.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
                <div dangerouslySetInnerHTML={{ __html: lpDuoHtml(people) + heroMoreHtml() }} />
              </div>
              <div id="zamow" className="lp-form-wrap">
                {form}
              </div>
            </div>
          </section>
        )}

        {!cennik && reviewsBlock && (
          <section className="lp-sec lp-reviews" id="dalej">
            <div className="lp-wrap" dangerouslySetInnerHTML={{ __html: reviewsBlock }} />
          </section>
        )}

        <div
          dangerouslySetInnerHTML={{
            __html: cennik
              ? priceCardsHtml(services) + includedHtml(landing) + stepsHtml(services, landing.howNote)
              : receiveHtml(services, landing.kind, shared, !reviewsBlock) + stepsHtml(services, landing.howNote),
          }}
        />

        {people.length > 0 && (
          <section className="lp-sec">
            <div className="lp-wrap">
              <h2>Kto przygotowuje odpowiedź</h2>
              <p className="lp-sub">
                {firmName
                  ? `Sprawy prowadzą radcowie prawni i adwokaci współpracujący z platformą — zespół ${firmName}${firmName.endsWith(".") ? "" : "."}`
                  : "Sprawy prowadzą radcowie prawni i adwokaci współpracujący z platformą."}
              </p>
              <TeamSlider people={people} />
            </div>
          </section>
        )}

        {cennik && reviewsBlock && (
          <section className="lp-sec lp-reviews">
            <div className="lp-wrap" dangerouslySetInnerHTML={{ __html: reviewsBlock }} />
          </section>
        )}

        {landing.topics?.length > 0 && (
          <section className="lp-sec lp-alt">
            <div className="lp-wrap">
              <h2>{landing.topicsTitle || "W jakich sprawach pomagamy"}</h2>
              <ul className="lp-topics">
                {landing.topics.map((t) => (
                  <li key={t}>{t}</li>
                ))}
              </ul>
              <p className="lp-topics-note">{TOPICS_NOTE}</p>
            </div>
          </section>
        )}

        {landing.faq?.length > 0 && (
          <section className="lp-sec">
            <div className="lp-wrap lp-narrow">
              <h2>Najczęstsze pytania</h2>
              {landing.faq.map((f) => (
                <details className="lp-faq" key={f.q}>
                  <summary>{f.q}</summary>
                  <p>{fillTerm(f.a, services)}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        <section className="lp-sec lp-cta">
          <div className="lp-wrap">
            <h2>Opisz sprawę — radca prawny przygotuje odpowiedź</h2>
            <p>
              {line ? `${line}.` : "Cenę i termin zobaczysz przed płatnością."}
            </p>
            <a href="#zamow" className="btn wax" onClick={goToForm}>
              Przejdź do formularza
            </a>
          </div>
        </section>
      </main>

      <footer className="lp-foot">
        <div className="lp-wrap">
          <div className="lp-foot-top">
            <Link to="/" className="logo" aria-label={`${settings.brand} — strona główna`}>
              <b>
                {settings.brand}
                <i>.</i>
              </b>
            </Link>
            <nav className="lp-foot-nav" aria-label="Informacje">
              <Link to="/regulamin">Regulamin</Link>
              <Link to="/polityka-prywatnosci">Polityka prywatności</Link>
              <Link to="/kontakt">Kontakt</Link>
              <Link to="/cennik">Cennik</Link>
              <button type="button" onClick={resetConsent}>
                Ustawienia cookies
              </button>
            </nav>
          </div>

          <div className="lp-foot-mid">
            {firmName && (
              <p>
                <b className="lp-foot-label">Obsługa prawna serwisu:</b> {firmName}
              </p>
            )}
            <p>
              <a href={`mailto:${settings.email}`}>{settings.email}</a>
              {settings.phone && (
                <>
                  {" · "}
                  <a href={phoneHref}>{settings.phone}</a>
                </>
              )}
              {settings.hours && ` · ${settings.hours}`}
            </p>
          </div>

        </div>
      </footer>

      {/* Szeroki ekran: po przewinięciu poza hero wjeżdża na górę pasek z przyciskiem
          "Zamów" (na telefonie tę rolę pełni przyklejony przycisk na dole). */}
      <div className={`lp-topbar${stickyOn ? " on" : ""}`} aria-hidden={!stickyOn}>
        <div className="lp-wrap lp-head-in">
          <Link to="/" className="logo" tabIndex={stickyOn ? 0 : -1} aria-label={`${settings.brand} — strona główna`}>
            <b>
              {settings.brand}
              <i>.</i>
            </b>
          </Link>
          <div className="lp-head-act">
            <OfferTicker services={services} />
            <a href="#zamow" className="btn wax sm" onClick={goToForm} tabIndex={stickyOn ? 0 : -1}>
              Zamów
            </a>
          </div>
        </div>
      </div>

      <div className={`lp-sticky${stickyOn ? " on" : ""}`} aria-hidden={!stickyOn}>
        <a href="#zamow" className="btn wax" onClick={goToForm} tabIndex={stickyOn ? 0 : -1}>
          Opisz sprawę{fromCents ? ` — od ${zl(fromCents)}` : ""}
        </a>
      </div>
    </div>
  );
}
